import React from 'react';
import { Card, Timeline, Typography, Avatar, Empty } from 'antd';
import {
  PlusOutlined,
  TrophyOutlined,
  RiseOutlined,
  FireOutlined,
  UserAddOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';

const { Title, Text } = Typography;

const StyledCard = styled(Card)`
  max-height: 600px;
  overflow-y: auto;

  .activity-item {
    display: flex;
    gap: 12px;
    padding: 12px 0;
  }
  .activity-icon {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;

    &.species-added { background: #e6f7ff; color: #1890ff; }
    &.collection-created { background: #f6ffed; color: #52c41a; }
    &.achievement-unlocked { background: #fff7e6; color: #fa8c16; }
    &.level-up { background: #f9f0ff; color: #722ed1; }
    &.streak-milestone { background: #fff2e8; color: #ff4d4f; }
    &.followed-user { background: #f0f0f0; color: #8c8c8c; }
  }
  .activity-content {
    flex: 1;
  }
  .activity-time {
    font-size: 12px;
    color: #8c8c8c;
  }
`;

interface Activity {
  _id: string;
  user: {
    _id: string;
    name: string;
    email: string;
  };
  activity_type: 'species_added' | 'collection_created' | 'achievement_unlocked' | 'level_up' | 'streak_milestone' | 'followed_user';
  metadata: any;
  created_at: string;
}

interface ActivityFeedProps {
  activities: Activity[];
  loading?: boolean;
}

const getActivityIcon = (type: string) => {
  switch (type) {
    case 'species_added':
      return <PlusOutlined />;
    case 'collection_created':
      return <PlusOutlined />;
    case 'achievement_unlocked':
      return <TrophyOutlined />;
    case 'level_up':
      return <RiseOutlined />;
    case 'streak_milestone':
      return <FireOutlined />;
    case 'followed_user':
      return <UserAddOutlined />;
    default:
      return <PlusOutlined />;
  }
};

const getActivityText = (activity: Activity) => {
  const { activity_type, metadata, user } = activity;

  switch (activity_type) {
    case 'species_added':
      return (
        <>
          <Text strong>{user.name}</Text> added{' '}
          <Text type="secondary">{metadata.species_name || 'a species'}</Text>
        </>
      );
    case 'collection_created':
      return (
        <>
          <Text strong>{user.name}</Text> created a new collection
        </>
      );
    case 'achievement_unlocked':
      return (
        <>
          <Text strong>{user.name}</Text> unlocked{' '}
          <Text type="warning">{metadata.achievement_icon} {metadata.achievement_name}</Text>
        </>
      );
    case 'level_up':
      return (
        <>
          <Text strong>{user.name}</Text> reached{' '}
          <Text type="success">Level {metadata.new_level}</Text>
        </>
      );
    case 'streak_milestone':
      return (
        <>
          <Text strong>{user.name}</Text> achieved a{' '}
          <Text type="danger">{metadata.streak_days}-day streak!</Text>
        </>
      );
    case 'followed_user':
      return (
        <>
          <Text strong>{user.name}</Text> followed a new user
        </>
      );
    default:
      return <Text>{user.name} performed an action</Text>;
  }
};

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  activities,
  loading = false
}) => {
  return (
    <StyledCard
      bordered={false}
      loading={loading}
      title={<Title level={4} style={{ margin: 0 }}>Activity Feed</Title>}
    >
      {activities.length === 0 ? (
        <Empty description="No recent activity" />
      ) : (
        <Timeline>
          {activities.map((activity) => (
            <Timeline.Item
              key={activity._id}
              dot={
                <div className={`activity-icon ${activity.activity_type}`}>
                  {getActivityIcon(activity.activity_type)}
                </div>
              }
            >
              <div className="activity-content">
                <div>{getActivityText(activity)}</div>
                <div className="activity-time">
                  {moment(activity.created_at).fromNow()}
                </div>
              </div>
            </Timeline.Item>
          ))}
        </Timeline>
      )}
    </StyledCard>
  );
};

export default ActivityFeed;
